import { supabase } from '../lib/supabase';
import roleService from './roleService';

const holidayService = {
  // Get all public holidays
  async getAllHolidays() {
    try {
      const { data, error } = await supabase?.from('public_holidays')?.select('*')?.order('holiday_date', { ascending: true });

      if (error) throw error;

      // Convert snake_case to camelCase
      return data?.map(holiday => ({
        id: holiday?.id,
        holidayName: holiday?.holiday_name,
        holidayDate: holiday?.holiday_date,
        holidayType: holiday?.holiday_type,
        description: holiday?.description,
        isRecurring: holiday?.is_recurring,
        createdBy: holiday?.created_by,
        createdAt: holiday?.created_at,
        updatedAt: holiday?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching holidays:', error);
      throw error;
    }
  },

  // Get holidays for a specific year
  async getHolidaysByYear(year) {
    try {
      const startDate = `${year}-01-01`;
      const endDate = `${year}-12-31`;

      const { data, error } = await supabase?.from('public_holidays')?.select('*')?.gte('holiday_date', startDate)?.lte('holiday_date', endDate)?.order('holiday_date', { ascending: true });

      if (error) throw error;

      return data?.map(holiday => ({
        id: holiday?.id,
        holidayName: holiday?.holiday_name,
        holidayDate: holiday?.holiday_date,
        holidayType: holiday?.holiday_type,
        description: holiday?.description,
        isRecurring: holiday?.is_recurring,
        createdBy: holiday?.created_by,
        createdAt: holiday?.created_at,
        updatedAt: holiday?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching holidays by year:', error);
      throw error;
    }
  },

  // Create a new public holiday (admin/HR only)
  async createHoliday(holidayData) {
    try {
      // Verify admin/HR permissions
      const canManage = await roleService?.canApproveLeaves();
      if (!canManage) {
        throw new Error('Insufficient permissions. Only admin/HR can add holidays.');
      }

      // Get current user
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Convert camelCase to snake_case for database
      const { data, error } = await supabase?.from('public_holidays')?.insert([{
          holiday_name: holidayData?.holidayName,
          holiday_date: holidayData?.holidayDate,
          holiday_type: holidayData?.holidayType || 'national',
          description: holidayData?.description || '',
          is_recurring: holidayData?.isRecurring || false,
          created_by: user?.id
        }])?.select()?.single();

      if (error) throw error;

      // Convert back to camelCase
      return {
        id: data?.id,
        holidayName: data?.holiday_name,
        holidayDate: data?.holiday_date,
        holidayType: data?.holiday_type,
        description: data?.description,
        isRecurring: data?.is_recurring,
        createdBy: data?.created_by,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error creating holiday:', error);
      throw error;
    }
  },

  // Update an existing holiday (admin/HR only)
  async updateHoliday(holidayId, holidayData) {
    try {
      // Verify admin/HR permissions
      const canManage = await roleService?.canApproveLeaves();
      if (!canManage) {
        throw new Error('Insufficient permissions. Only admin/HR can update holidays.');
      }

      // Convert camelCase to snake_case
      const updateData = {};
      if (holidayData?.holidayName !== undefined) updateData.holiday_name = holidayData?.holidayName;
      if (holidayData?.holidayDate !== undefined) updateData.holiday_date = holidayData?.holidayDate;
      if (holidayData?.holidayType !== undefined) updateData.holiday_type = holidayData?.holidayType;
      if (holidayData?.description !== undefined) updateData.description = holidayData?.description;
      if (holidayData?.isRecurring !== undefined) updateData.is_recurring = holidayData?.isRecurring;
      updateData.updated_at = new Date()?.toISOString();

      const { data, error } = await supabase?.from('public_holidays')?.update(updateData)?.eq('id', holidayId)?.select()?.single();

      if (error) throw error;

      // Convert back to camelCase
      return {
        id: data?.id,
        holidayName: data?.holiday_name,
        holidayDate: data?.holiday_date,
        holidayType: data?.holiday_type,
        description: data?.description,
        isRecurring: data?.is_recurring,
        createdBy: data?.created_by,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating holiday:', error);
      throw error;
    }
  },

  // Delete a holiday (admin/HR only)
  async deleteHoliday(holidayId) {
    try {
      // Verify admin/HR permissions
      const canManage = await roleService?.canApproveLeaves();
      if (!canManage) {
        throw new Error('Insufficient permissions. Only admin/HR can delete holidays.');
      }

      const { error } = await supabase?.from('public_holidays')?.delete()?.eq('id', holidayId);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error deleting holiday:', error);
      throw error;
    }
  },

  // Check if a specific date is a public holiday
  async isPublicHoliday(date) {
    try {
      const { data, error } = await supabase?.from('public_holidays')?.select('holiday_name')?.eq('holiday_date', date)?.single();

      if (error && error?.code !== 'PGRST116') throw error; // PGRST116 = no rows found

      return data ? { isHoliday: true, holidayName: data?.holiday_name } : { isHoliday: false };
    } catch (error) {
      console.error('Error checking holiday:', error);
      return { isHoliday: false };
    }
  }
};

export default holidayService;